/**
 * This file is adapted from:
 *  https://github.com/DJanoskova/React-validator-demo/blob/master/src/utils/validator.js
 * The original author didn't release a npm module yet.
 * We added sufficient amount of changes to adapt to our use cases.
 */
import {
  useCallback, useEffect, useState,
} from 'react';
import validator from 'validator';

/**
 * Returns either unmet rule, or null
 * @param value
 * @param validation
 * @returns {*}
 */
export function validate(value, validation) {
  const fields = {};
  let trimmedValidation;

  switch (typeof validation) {
    case 'object':
      Object.keys(validation).forEach(property => (fields[property] = validation[property]));
      break;

    case 'string':
    default:
      if (!validation.length) return null;
      trimmedValidation = validation.replace(/ /g, '');
      trimmedValidation.split(',').forEach(name => (fields[name.trim()] = true));
  }

  const isRequired = fields.isRequired || fields.isEmpty === false;
  if (!value && !isRequired) {
    return null;
  }
  let isValid = true;
  let failedRule = null;

  Object.keys(fields).forEach(rule => {
    if (!isValid) {
      return;
    }
    const options = fields[rule];

    switch (rule) {
      case 'isRequired':
        if (!value) {
          isValid = false;
        }
        break;
      default:
        if (!options || options === true) {
          isValid = validator[rule](value);
        } else if (options && typeof options === 'function') {
          isValid = options(value);
        } else {
          isValid = validator[rule](value, options);
        }
    }
    if (!isValid) {
      failedRule = rule;
    }
  });

  return failedRule || null;
}

export function useFormInput({
  name,
  formHandler,
  validation = '',
  handleError,
  lazy,
  defaultInvalidAttr,
}) {
  const [formData, setFormData] = formHandler;
  const formValue = formData[name] || '';

  const [value, setValue] = useState(formValue);
  const [failedRule, setFailedRule] = useState(null);
  const [isTouched, setIsTouched] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const handleValidation = useCallback(val => {
    const result = validate(val, validation);
    setFailedRule(result);
    handleError(name, result);
    return !result;
  }, [validation, handleError, name]);

  // validation
  useEffect(() => {
    !lazy && handleValidation(value);
  }, [lazy, handleValidation, value]);

  // watch for external parent data changes
  useEffect(() => {
    if (value !== formValue) {
      setValue(formValue);
      setIsTouched(false);
      setIsFocused(false);
    }
  }, [formValue, value, setValue, setIsFocused, setIsTouched]);

  // rewrite self and parent's value
  function handleChange({ target }) {
    const { value: val, checked, type } = target;
    const newValue = type === 'checkbox' ? checked : val;

    setValue(newValue);
    setFormData({ ...formData, [name]: newValue });
  }

  function handleFocus() {
    if (!isTouched) {
      setIsTouched(true);
    }
    setIsFocused(true);
    !lazy && handleValidation(value);
  }

  function handleBlur() {
    setIsFocused(false);
  }

  const showError = failedRule && ((isTouched && !isFocused) || lazy);
  const invalidAttr = showError ? defaultInvalidAttr : null;

  const fields = {
    value,
    name,
    onChange: handleChange,
    onFocus: handleFocus,
    onBlur: handleBlur,
    ...invalidAttr,
  };

  return [handleValidation, fields];
}

export function useForm(defaultValues, { invalidAttr = { 'data-error': 'show' }, lazy = false } = {}) {
  const formHandler = useState(defaultValues);
  const errorHandler = useState({});
  const [mounted, setMounted] = useState(false);

  const [values, setValues] = formHandler;
  const [errors, setErrors] = errorHandler;

  useEffect(() => setMounted(true), []);

  const handleError = (name, failedRule) => {
    if (!failedRule) {
      delete errors[name];
    } else {
      errors[name] = failedRule;
    }
    setErrors(errors);
  };

  const validators = {};
  const useInput = (name, validation) => {
    const [handleValidation, fields] = useFormInput({
      name,
      validation,
      formHandler,
      handleError,
      lazy,
      defaultInvalidAttr: invalidAttr,
    });
    validators[name] = handleValidation;
    return fields;
  };

  const reducer = (result, name) => validators[name] && validators[name](values[name]) && result;
  const validateForm = () => Object.keys(values).reduce(reducer, true);

  return {
    values,
    setValues,
    useInput,
    errors,
    validateForm,
    isValid: mounted && !Object.values(errors).length,
  };
}
